// src/components/ClothingStorePage.jsx
//
// ── Design note ──────────────────────────────────────────────────────────
// Plain retail store page — no fantasy/game copy, no roleplay branding.
// It does not render its own navbar: the global <Navbar/> is hidden on
// this page from App.jsx (page === "merch"), and this page shows its own
// store logo and account/cart controls instead. It reuses
// the CSS custom properties (--bg, --gold, --purple, etc.) from global.css
// so it still feels like part of the same site.
//
// LOGO: drop your SVG logo into the `public` folder (e.g. public/logo.svg)
// and it will render at the top of the store. Update LOGO_SRC below if you
// name the file something else or host it elsewhere.
//
// ── Live products & cart (Fourthwall Storefront API) ───────────────────────
// Products, colours/sizes, images, and prices are now fetched live from
// Fourthwall via src/utils/fourthwall.js — nothing is hardcoded here
// anymore. "Add to Cart" builds a real Fourthwall cart entirely on this
// page; the customer only ever leaves the site for the final hosted
// checkout screen (Fourthwall has to own payment + fulfillment).
//
// Setup required:
//   1. Copy .env.example to .env and fill in REACT_APP_FW_STOREFRONT_TOKEN
//      and REACT_APP_FW_CHECKOUT_DOMAIN, then restart `npm start`.
//   2. Each entry in COLLECTIONS below needs a `fourthwallHandle` that
//      matches the collection's handle/slug in your Fourthwall dashboard
//      (Products > Collections). It defaults to the entry's `id` — change
//      it if your Fourthwall collection is named differently.
// ────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Heart,
  ShoppingBag,
  ChevronRight,
  ChevronLeft,
  X,
  Minus,
  Plus,
  Trash2,
  UserRound,
  LogIn,
  Settings,
  LogOut,
  ChevronDown,
  Share2,
  Check,
  Copy,
} from "lucide-react";
import * as fourthwall from "../utils/fourthwall";
import Footer from "./Footer";

// Path to the store logo — place the actual file in /public.
const LOGO_SRC = "/logo.svg";

// ─── Geometry ───────────────────────────────────────────────────────────────
// Small geometric tile used only for the faint background lattice texture.
const STAR_TILE  = "30,15 32.53,23.9 40.61,19.39 36.1,27.47 45,30 36.1,32.53 40.61,40.61 32.53,36.1 30,45 27.47,36.1 19.39,40.61 23.9,32.53 15,30 23.9,27.47 19.39,19.39 27.47,23.9";

// ─── CSS injected once ──────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Reem+Kufi:wght@400..700&display=swap');

  @keyframes store-fade-up { from{ opacity:0; transform:translateY(24px);} to{ opacity:1; transform:translateY(0);} }
  @keyframes store-pulse   { 0%,100%{ box-shadow:0 0 0 0 rgba(201,168,76,0);} 50%{ box-shadow:0 0 20px 3px rgba(201,168,76,.22);} }
  @keyframes store-glint   { 0%{ transform:translateX(-120%) skewX(-12deg);} 100%{ transform:translateX(220%) skewX(-12deg);} }
  @keyframes store-spin    { to{ transform:rotate(360deg); } }

  .store-reveal{ animation:store-fade-up .6s cubic-bezier(.22,.68,0,1.05) both; }

  @media (prefers-reduced-motion: reduce){
    .store-reveal{ animation:none !important; }
  }

  .store-card{ transition:transform .3s cubic-bezier(.22,.68,0,1.05), box-shadow .3s ease, border-color .3s ease; cursor:pointer; position:relative; }
  .store-card:hover{ transform:translateY(-6px); box-shadow:0 24px 60px rgba(0,0,0,.65); }
  .store-card:hover .store-corner{ opacity:1 !important; }
  .store-card:hover .store-cta{ clip-path:inset(0 0 0 0) !important; opacity:1 !important; }
  .store-card:hover .store-glint::after{ animation:store-glint 1.1s ease; }
  .store-glint::after{ content:""; position:absolute; top:0; bottom:0; left:0; width:40%; background:linear-gradient(100deg,transparent,rgba(255,255,255,.08),transparent); }

  .store-chapter{ transition:transform .32s cubic-bezier(.22,.68,0,1.05), box-shadow .32s ease; cursor:pointer; }
  .store-chapter:hover{ transform:translateY(-5px); box-shadow:0 26px 70px rgba(0,0,0,.75); }
  .store-chapter:hover .store-corner{ opacity:1 !important; }
  .store-chapter:hover .store-chev{ transform:translateX(5px) !important; }

  .store-swatch{ transition:transform .15s ease; }
  .store-swatch:hover{ transform:scale(1.18); }
  .store-size:hover{ border-color:rgba(201,168,76,.55) !important; color:var(--goldL) !important; }
  .store-tab:hover .store-tab-line{ opacity:1 !important; }
  .store-home:hover{ color:var(--goldL) !important; }
  .store-back:hover{ color:var(--goldL) !important; opacity:1 !important; }
  .store-cart-btn:hover{ border-color:rgba(201,168,76,.5) !important; }
  .store-account-btn:hover{ border-color:rgba(201,168,76,.5) !important; }

  .store-login-mobile{ display:none; }

  .store-top-actions{
    flex-wrap:nowrap;
    white-space:nowrap;
  }
  .store-account-wrap{
    position:relative;
    z-index:45;
  }
  .store-account-menu{
    position:absolute;
    top:calc(100% + 8px);
    right:0;
    min-width:170px;
    padding:6px;
    background:rgba(12,8,20,.98);
    border:1px solid var(--borderB);
    border-radius:8px;
    box-shadow:0 18px 45px rgba(0,0,0,.55);
    backdrop-filter:blur(12px);
    transform-origin:top right;
    animation:store-account-menu-in .16s ease both;
    z-index:60;
  }
  .store-account-menu::before{
    content:"";
    position:absolute;
    left:0;
    right:0;
    top:-12px;
    height:12px;
    background:transparent;
  }
  .store-account-menu-btn{
    width:100%;
    border:0;
    background:transparent;
    color:var(--text2);
    padding:10px 11px;
    border-radius:6px;
    cursor:pointer;
    display:flex;
    align-items:center;
    gap:9px;
    font-family:'Reem Kufi',sans-serif;
    font-size:11px;
    letter-spacing:.6px;
    text-align:left;
  }
  .store-account-menu-btn:hover{
    background:rgba(123,94,167,.12);
    color:var(--text);
  }
  .store-account-menu-btn.logout:hover{
    background:rgba(190,70,70,.10);
    color:#efb0b0;
  }
  @keyframes store-account-menu-in{
    from{opacity:0;transform:translateY(-5px) scale(.98);}
    to{opacity:1;transform:translateY(0) scale(1);}
  }
  .store-cart-overlay{ animation:store-cart-overlay-in .2s ease both; }
  .store-cart-drawer{ animation:store-cart-drawer-in .32s cubic-bezier(.22,.68,0,1.02) both; }
  .store-cart-icon-btn:hover{ border-color:rgba(201,168,76,.45) !important; color:var(--goldL) !important; }
  .store-cart-checkout:hover{ transform:translateY(-1px); box-shadow:0 12px 32px rgba(201,168,76,.28) !important; }
  .store-product-overlay{ animation:store-cart-overlay-in .18s ease both; }
  .store-product-modal{ animation:store-product-modal-in .28s cubic-bezier(.22,.68,0,1.03) both; }
  .store-product-thumb{ transition:border-color .16s ease, opacity .16s ease, transform .16s ease; }
  .store-product-thumb:hover{ transform:translateY(-2px); border-color:rgba(201,168,76,.5) !important; }
  .store-product-choice:hover{ border-color:rgba(201,168,76,.55) !important; color:var(--goldL) !important; }
  .store-spinner{ animation:store-spin .8s linear infinite; }

  .store-product-description{
    color:var(--text2);
    font-size:12px;
    line-height:1.85;
  }
  .store-product-description p{
    margin:0 0 12px;
  }
  .store-product-description p:last-child{
    margin-bottom:0;
  }
  .store-product-description ul,
  .store-product-description ol{
    margin:10px 0 0;
    padding-left:20px;
  }
  .store-product-description li{
    margin:5px 0;
    padding-left:2px;
  }
  .store-product-description strong,
  .store-product-description b{
    color:var(--text);
    font-weight:700;
  }

  @keyframes store-product-modal-in{
    from{ opacity:0; transform:translateY(18px) scale(.985); }
    to{ opacity:1; transform:translateY(0) scale(1); }
  }

  @keyframes store-cart-overlay-in{ from{opacity:0;} to{opacity:1;} }
  @keyframes store-cart-drawer-in{ from{transform:translateX(100%);} to{transform:translateX(0);} }

  a.store-cta:focus-visible, button.store-tab:focus-visible, button.store-size:focus-visible,
  button.store-swatch:focus-visible, .store-home:focus-visible, .store-back:focus-visible,
  .store-cart-btn:focus-visible, .store-account-btn:focus-visible{
    outline:1.5px solid var(--goldL); outline-offset:3px; border-radius:4px;
  }

  /* Pull the store hero upward. The SVG logo contains transparent canvas
     space, so the wrapper intentionally compensates for that whitespace. */
  .store-hero-header{
    padding-top:0 !important;
    padding-bottom:52px !important;
  }

  .store-logo-wrap{
    margin-top:clamp(-120px,-8vw,-72px) !important;
    margin-bottom:clamp(-205px,-13vw,-118px) !important;
  }

  @media (max-width:720px){
    .store-hero-header{
      padding-bottom:42px !important;
    }

    .store-logo-wrap{
      margin-top:-88px !important;
      margin-bottom:-118px !important;
    }
  }

  @media (max-width:460px){
    .store-hero-header{
      padding-bottom:36px !important;
    }

    .store-logo-wrap{
      margin-top:-76px !important;
      margin-bottom:-104px !important;
    }
  }

  @media (max-width:720px){
    .store-manifesto{ display:none !important; }

    .store-top-actions{ top:12px !important; right:12px !important; gap:7px !important; }
    .store-account-btn, .store-cart-btn{
      height:38px !important;
      padding-left:12px !important;
      padding-right:12px !important;
      font-size:10px !important;
    }

    .store-account-menu{
      top:calc(100% + 6px) !important;
      min-width:160px !important;
    }
    .store-account-menu::before{
      top:-10px !important;
      height:10px !important;
    }

    /* Product detail becomes a proper full-screen mobile sheet. */
    .store-product-overlay{
      padding:0 !important;
      display:block !important;
      overflow:hidden !important;
    }

    .store-product-modal{
      width:100% !important;
      height:100dvh !important;
      max-height:100dvh !important;
      min-height:100dvh !important;
      border-radius:0 !important;
      border-left:0 !important;
      border-right:0 !important;
      overflow-y:auto !important;
      overscroll-behavior:contain;
    }

    .store-product-grid{
      display:block !important;
      min-height:0 !important;
    }

    .store-product-gallery{
      min-height:0 !important;
      padding:58px 14px 14px !important;
      border-right:0 !important;
      border-bottom:1px solid var(--border) !important;
    }

    .store-product-image-main{
      height:auto !important;
      min-height:0 !important;
      aspect-ratio:1 / 1.15 !important;
      padding:12px !important;
      overflow:hidden !important;
    }

    .store-product-image-main img{
      width:auto !important;
      height:auto !important;
      max-width:100% !important;
      max-height:100% !important;
      object-fit:contain !important;
      object-position:center !important;
      display:block !important;
      margin:auto !important;
    }

    .store-product-info{
      padding:24px 18px 32px !important;
    }

    .store-product-modal-close{
      position:fixed !important;
      top:12px !important;
      right:12px !important;
      width:38px !important;
      height:38px !important;
      z-index:130 !important;
      background:rgba(8,6,14,.92) !important;
    }
  }

  @media (max-width:460px){
    /* Keep Login and Cart as two proper pills side-by-side.
       Only the logged-in account button collapses to an icon circle. */
    .store-top-actions{
      top:10px !important;
      right:10px !important;
      left:10px !important;
      justify-content:flex-end !important;
      gap:6px !important;
      flex-wrap:nowrap !important;
    }

    .store-account-btn span.store-account-name{
      display:none;
    }

    .store-account-btn:not(.store-login-btn){
      width:38px !important;
      min-width:38px !important;
      padding:0 !important;
      justify-content:center !important;
    }

    .store-account-btn:not(.store-login-btn) .store-account-chevron{
      display:none;
    }

    .store-login-btn{
      width:auto !important;
      min-width:0 !important;
      height:38px !important;
      padding:0 11px !important;
      gap:6px !important;
      justify-content:center !important;
      flex-shrink:1 !important;
      overflow:hidden !important;
    }

    .store-login-btn .store-login-desktop{
      display:inline;
    }

    .store-login-btn .store-login-mobile{
      display:none;
    }

    .store-cart-btn{
      width:auto !important;
      min-width:max-content !important;
      height:38px !important;
      padding:0 11px !important;
      gap:6px !important;
      flex-shrink:0 !important;
    }

    .store-product-gallery{
      padding:54px 10px 10px !important;
    }

    .store-product-image-main{
      height:auto !important;
      min-height:0 !important;
      aspect-ratio:1 / 1.15 !important;
      padding:10px !important;
    }

    .store-product-info{
      padding:20px 15px 28px !important;
    }

    .store-product-info h2{
      font-size:26px !important;
    }
  }

  /* On especially narrow phones, shorten only the wording.
     The button remains a pill and stays to the left of Cart. */
  @media (max-width:380px){
    .store-login-btn{
      padding:0 10px !important;
    }

    .store-login-btn .store-login-desktop{
      display:none !important;
    }

    .store-login-btn .store-login-mobile{
      display:inline !important;
    }

    .store-cart-btn{
      padding:0 10px !important;
    }
  }
`;

// ─── Small motifs ───────────────────────────────────────────────────────────

// Corner bracket accent — used on card hover, four rotations.
function CornerMark({ rotate = 0 }) {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden
      style={{ position: "absolute", opacity: 0, transition: "opacity .25s ease", pointerEvents: "none", transform: `rotate(${rotate}deg)` }}
      className="store-corner"
    >
      <path d="M2 14 V6 Q2 2 6 2 H14" fill="none" stroke="var(--goldL)" strokeWidth="1.1" />
      <circle cx="2" cy="20" r="1.4" fill="var(--goldL)" opacity="0.8" />
      <circle cx="20" cy="2" r="1.4" fill="var(--goldL)" opacity="0.8" />
    </svg>
  );
}

// Fixed, very-low-opacity girih lattice sitting behind the whole page.
function GirihField() {
  return (
    <svg aria-hidden style={{ position: "fixed", inset: 0, width: "100%", height: "100%", opacity: 0.05, pointerEvents: "none", zIndex: 0 }}>
      <defs>
        <pattern id="girih-tile" width="60" height="60" patternUnits="userSpaceOnUse">
          <polygon points={STAR_TILE} fill="none" stroke="var(--goldL)" strokeWidth="0.6" />
          <line x1="0" y1="30" x2="60" y2="30" stroke="var(--purpleB)" strokeWidth="0.3" />
          <line x1="30" y1="0" x2="30" y2="60" stroke="var(--purpleB)" strokeWidth="0.3" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#girih-tile)" />
    </svg>
  );
}

// Thin repeating geometric divider strip, stands in for a plain <hr>.
function Frieze() {
  const tiles = Array.from({ length: 22 });
  return (
    <div aria-hidden style={{ display: "flex", justifyContent: "center", gap: 22, padding: "0 20px", overflow: "hidden" }}>
      {tiles.map((_, i) => (
        <svg key={i} width="10" height="10" viewBox="0 0 10 10" style={{ flexShrink: 0, opacity: i % 3 === 0 ? 0.55 : 0.22 }}>
          <polygon points="5,0 6.5,3.5 10,5 6.5,6.5 5,10 3.5,6.5 0,5 3.5,3.5"
            fill="none" stroke={i % 3 === 0 ? "var(--goldL)" : "var(--purpleB)"} strokeWidth="0.8" />
        </svg>
      ))}
    </div>
  );
}

function FadeIn({ children, delay = 0, style = {} }) {
  return (
    <div className="store-reveal" style={{ animationDelay: `${delay}ms`, ...style }}>
      {children}
    </div>
  );
}

// ─── Collections (display metadata only — products come from Fourthwall) ──
// `fourthwallHandle` must match the collection's handle in your Fourthwall
// dashboard. Add more entries here as you create more live collections.
const COLLECTIONS = [
  {
    id: "qadr",
    fourthwallHandle: "qadr-collection",
    name: "Qadr",
    arabic: "القدر",
    label: "Collection I",
    tagline: "",
    desc: "Destiny is not given — it is worn. 100% of profits from the Qadr Collection go to charity.",
    charity: true,
    status: "live",
    accentColor: "#c9a84c",
    accentGlow: "rgba(201,168,76,.25)",
    bg: "linear-gradient(145deg,#110828 0%,#1e0d40 40%,#0c0618 100%)",
    borderColor: "rgba(201,168,76,.3)",
  },
  {
    id: "coming1",
    fourthwallHandle: null,
    name: "???",
    arabic: "؟",
    label: "Collection II",
    tagline: "Coming soon.",
    desc: "The next drop is being prepared. Stay close.",
    charity: false,
    status: "soon",
    accentColor: "#7b5ea7",
    accentGlow: "rgba(123,94,167,.2)",
    bg: "linear-gradient(145deg,#0a0818 0%,#130a28 50%,#080614 100%)",
    borderColor: "rgba(123,94,167,.2)",
  },
];

const CATEGORIES = ["all", "hats", "hoodies", "tees", "sets", "other"];
const TILE_COLORS = ["var(--goldL)", "var(--purpleB)", "var(--goldL)", "var(--purpleB)", "var(--goldL)", "var(--goldL)"];

// Temporary manual merchandising until the admin dashboard controls this.
// Use the Fourthwall product slug OR product id as `match`.
// Example:
// qadr: [
//   { match: "qadr-hoodie", label: "Best Seller" },
//   { match: "another-product-id", label: "Most Popular" },
// ],
const PRODUCT_HIGHLIGHTS = {
  qadr: [
    {
      match: "its-written-hoodie",
      label: "Most Popular",
      priority: 100,
    },
  ],
};

function getProductHighlight(collectionId, product) {
  const rules = PRODUCT_HIGHLIGHTS[collectionId] || [];

  return rules.find((rule) => {
    const match = String(rule?.match || "").trim();
    if (!match) return false;

    return (
      String(product?.id || "") === match ||
      String(product?.slug || "") === match
    );
  }) || null;
}


function getProductHighlightPriority(collectionId, product) {
  const highlight = getProductHighlight(collectionId, product);
  return Number(highlight?.priority || 0);
}

// Fourthwall products don't carry a "category" field, so this makes a best
// guess from the product name for the filter tabs. Tweak the keywords below
// if your naming doesn't match, or delete the tabs entirely if you'd rather
// not filter.
function inferCategories(name = "") {
  const n = name.toLowerCase();
  const categories = [];

  if (
    n.includes("hoodie") ||
    n.includes("zip hoodie") ||
    n.includes("zip-up")
  ) {
    categories.push("hoodies");
  }

  if (
    n.includes("tee") ||
    n.includes("t-shirt") ||
    n.includes("tshirt") ||
    n.includes("shirt")
  ) {
    categories.push("tees");
  }

  if (
    n.includes("cap") ||
    n.includes("hat") ||
    n.includes("beanie") ||
    n.includes("snapback")
  ) {
    categories.push("hats");
  }

  if (
    n.includes("tracksuit") ||
    n.includes("jogger") ||
    n.includes("joggers") ||
    n.includes("sweatpant") ||
    n.includes("sweatpants") ||
    n.includes("bottoms") ||
    n.includes(" set") ||
    n.endsWith("set")
  ) {
    categories.push("sets");
  }

  return categories.length ? [...new Set(categories)] : ["other"];
}

const EURO_CURRENCY_REGIONS = new Set([
  "AT", "BE", "BG", "HR", "CY", "EE", "FI", "FR", "DE", "GR", "IE",
  "IT", "LV", "LT", "LU", "MT", "NL", "PT", "SK", "SI", "ES",
]);

function detectStoreCurrency() {
  try {
    const locales = Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator.language].filter(Boolean);

    for (const locale of locales) {
      try {
        const region = new Intl.Locale(locale).region;

        if (region === "GB") return "GBP";
        if (EURO_CURRENCY_REGIONS.has(region)) return "EUR";
      } catch {}
    }

    // Useful fallback for UK devices whose browser language is set to en-US.
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone === "Europe/London") return "GBP";
    if (timeZone === "Europe/Dublin") return "EUR";
  } catch {}

  return "USD";
}


function stripProductHtml(html = "") {
  if (!html) return "";

  try {
    const doc = new DOMParser().parseFromString(String(html), "text/html");
    return (doc.body.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    return String(html)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
}

function sanitiseProductHtml(html = "") {
  if (!html) return "";

  try {
    const doc = new DOMParser().parseFromString(String(html), "text/html");

    const allowedTags = new Set([
      "P", "DIV", "UL", "OL", "LI", "BR",
      "STRONG", "B", "EM", "I", "A"
    ]);

    const walk = (node) => {
      [...node.children].forEach((child) => {
        if (!allowedTags.has(child.tagName)) {
          child.replaceWith(...child.childNodes);
          return;
        }

        [...child.attributes].forEach((attr) => {
          const name = attr.name.toLowerCase();

          if (child.tagName === "A" && name === "href") {
            const href = child.getAttribute("href") || "";
            if (!/^https?:\/\//i.test(href)) {
              child.removeAttribute("href");
            } else {
              child.setAttribute("target", "_blank");
              child.setAttribute("rel", "noopener noreferrer");
            }
            return;
          }

          child.removeAttribute(attr.name);
        });

        walk(child);
      });
    };

    walk(doc.body);

    return doc.body.innerHTML;
  } catch {
    return "";
  }
}

function ProductDescription({ html, compact = false }) {
  const [expanded, setExpanded] = useState(false);

  if (!html) return null;

  if (compact) {
    const text = stripProductHtml(html);

    if (!text) return null;

    return (
      <p
        style={{
          fontSize: 12,
          color: "var(--text2)",
          lineHeight: 1.7,
          margin: 0,
          flex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {text}
      </p>
    );
  }

  const safeHtml = sanitiseProductHtml(html);

  if (!safeHtml) {
    const text = stripProductHtml(html);

    return (
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            color: "var(--text2)",
            fontSize: 12,
            lineHeight: 1.85,
            display: expanded ? "block" : "-webkit-box",
            WebkitLineClamp: expanded ? "unset" : 4,
            WebkitBoxOrient: "vertical",
            overflow: expanded ? "visible" : "hidden",
          }}
        >
          {text}
        </div>

        {text.length > 180 && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            style={{
              marginTop: 10,
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "var(--goldL)",
              fontFamily: "'Reem Kufi',sans-serif",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: "uppercase",
            }}
          >
            {expanded ? "Read less" : "Read more"}
          </button>
        )}
      </div>
    );
  }

  const plainText = stripProductHtml(html);
  const shouldCollapse = plainText.length > 180;

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        className="store-product-description"
        style={{
          maxHeight:
            !expanded && shouldCollapse
              ? 105
              : "none",
          overflow:
            !expanded && shouldCollapse
              ? "hidden"
              : "visible",
          position: "relative",
        }}
        dangerouslySetInnerHTML={{
          __html: safeHtml,
        }}
      />

      {shouldCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          style={{
            marginTop: 10,
            padding: 0,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "var(--goldL)",
            fontFamily: "'Reem Kufi',sans-serif",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: "uppercase",
          }}
        >
          {expanded
            ? "Read less"
            : "Read more"}
        </button>
      )}
    </div>
  );
}

function isGroupedOrBundleProduct(product, variant) {
  const productType = String(product?.type || "").toUpperCase();
  const variantType = String(variant?.type || "").toUpperCase();

  return Boolean(
    productType === "BUNDLE" ||
    productType === "GROUPED" ||
    variantType === "BUNDLE" ||
    variantType === "GROUPED" ||
    product?.bundleId ||
    product?.groupedId ||
    variant?.bundleId ||
    variant?.groupedId
  );
}

function displayPriceValue(value, product, variant) {
  const n = Number(value);

  if (!Number.isFinite(n)) return value;

  // Fourthwall grouped/bundle products can sometimes expose a converted
  // storefront amount in minor-unit-like form. A merch item above £/$/€1000
  // is not expected in this store, so normalise those obvious outliers.
  if (
    n >= 1000 &&
    (
      isGroupedOrBundleProduct(product, variant) ||
      String(product?.name || "").toLowerCase().includes("set")
    )
  ) {
    return n / 100;
  }

  return n;
}

function formatPrice(value, currency = "USD") {
  try {
    const locale =
      currency === "GBP" ? "en-GB" :
      currency === "EUR" ? "en-IE" :
      "en-US";

    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}


function getStoreBaseUrl() {
  const url = new URL(window.location.href);
  url.search = "";
  url.hash = "";
  return url;
}

function buildCollectionUrl(collectionId) {
  const url = getStoreBaseUrl();
  url.searchParams.set("collection", collectionId);
  return url.toString();
}

function buildProductUrl(collectionId, productId) {
  const url = getStoreBaseUrl();
  url.searchParams.set("collection", collectionId);
  url.searchParams.set("product", productId);
  return url.toString();
}

function replaceStoreUrl({ collectionId = null, productId = null }, mode = "push") {
  const url = new URL(window.location.href);

  if (collectionId) {
    url.searchParams.set("collection", collectionId);
  } else {
    url.searchParams.delete("collection");
  }

  if (productId) {
    url.searchParams.set("product", productId);
  } else {
    url.searchParams.delete("product");
  }

  if (mode === "replace") {
    window.history.replaceState({}, "", url);
  } else {
    window.history.pushState({}, "", url);
  }
}

async function copyStoreLink(url) {
  return copyStoreLink(url);
}

async function shareStoreLink({ title, text, url }) {
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return "shared";
    } catch (err) {
      if (err?.name === "AbortError") {
        return "cancelled";
      }
    }
  }

  try {
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = url;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";

    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();

    return "copied";
  }
}

function ShareButton({
  url,
  title,
  text,
  compact = false,
  copyOnly = false,
}) {
  const [copied, setCopied] = useState(false);

  async function handleShare(e) {
    e?.preventDefault();
    e?.stopPropagation();

    const result = copyOnly
      ? await copyStoreLink(url)
      : await shareStoreLink({
          title,
          text,
          url,
        });

    if (result === "copied") {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  }

  return (
    <button
      type="button"
      onClick={handleShare}
      aria-label={
        copied
          ? "Link copied"
          : copyOnly
          ? "Copy link"
          : "Share"
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        height: compact ? 34 : 38,
        padding: compact ? "0 11px" : "0 14px",
        borderRadius: 999,
        cursor: "pointer",
        border: copied
          ? "1px solid rgba(201,168,76,.55)"
          : "1px solid var(--border)",
        background: copied
          ? "rgba(201,168,76,.10)"
          : "rgba(255,255,255,.025)",
        color: copied
          ? "var(--goldL)"
          : "var(--text2)",
        fontFamily: "'Reem Kufi',sans-serif",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1.1,
        textTransform: "uppercase",
        transition: ".2s ease",
        whiteSpace: "nowrap",
      }}
    >
      {copied ? (
        <Check style={{ width: 13, height: 13 }} />
      ) : copyOnly ? (
        <Copy style={{ width: 13, height: 13 }} />
      ) : (
        <Share2 style={{ width: 13, height: 13 }} />
      )}

      {copied
        ? "Copied"
        : copyOnly
        ? "Copy Link"
        : "Share"}
    </button>
  );
}

// ─── Product card ────────────────────────────────────────────────────────────
// Cards are deliberately simple. Clicking the product opens a detail modal,
// where the customer chooses the exact Fourthwall colour/size variant.
function ProductCard({ product, watermark, onOpenProduct, collectionId }) {
  const variants = product.variants || [];
  const highlight = getProductHighlight(collectionId, product);
  const firstPricedVariant = variants.find((v) => v?.unitPrice) || variants[0];
  const image =
    product.images?.[0]?.url ||
    product.images?.[0]?.transformedUrl ||
    firstPricedVariant?.images?.[0]?.url ||
    firstPricedVariant?.images?.[0]?.transformedUrl ||
    null;
  const price = firstPricedVariant?.unitPrice;

  return (
    <div
      className="store-card store-glint"
      role="button"
      tabIndex={0}
      onClick={() => onOpenProduct(product)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenProduct(product);
        }
      }}
      style={{
        background: "var(--card)", border: "1px solid var(--border)", borderRadius: 4,
        overflow: "hidden", display: "flex", flexDirection: "column", position: "relative",
      }}
    >
      <span style={{ position: "absolute", top: 8, left: 8, zIndex: 4 }}><CornerMark rotate={0} /></span>
      <span style={{ position: "absolute", top: 8, right: 8, zIndex: 4 }}><CornerMark rotate={90} /></span>

      {highlight && (
        <div
          style={{
            position: "absolute",
            top: 14,
            left: 14,
            zIndex: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "7px 11px",
            borderRadius: 999,
            background: "linear-gradient(135deg,var(--gold),#9b7228)",
            color: "#160d00",
            border: "1px solid rgba(255,255,255,.18)",
            boxShadow: "0 8px 24px rgba(0,0,0,.35)",
            fontFamily: "'Reem Kufi',sans-serif",
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: 1.4,
            textTransform: "uppercase",
          }}
        >
          ★ {highlight.label || "Best Seller"}
        </div>
      )}

      <div style={{
        height: 290, flexShrink: 0,
        background: "linear-gradient(145deg,#141018 0%,#1c1626 55%,#0e0a12 100%)",
        position: "relative", overflow: "hidden", display: "flex",
        alignItems: "center", justifyContent: "center",
      }}>
        {image ? (
          <img
            src={image}
            alt={product.name}
            loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div aria-hidden style={{
            fontFamily: "'Reem Kufi',sans-serif", fontSize: 64, color: "rgba(255,255,255,.06)",
            userSelect: "none", pointerEvents: "none", position: "relative", zIndex: 1, lineHeight: 1,
          }}>{watermark}</div>
        )}

        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, transparent, var(--goldL), transparent)", opacity: .75,
        }} />

        <div className="store-cta" style={{
          position: "absolute", inset: 0, background: "rgba(0,0,0,.48)", backdropFilter: "blur(2px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3,
          clipPath: "inset(0 100% 0 0)", opacity: 0,
          transition: "clip-path .32s cubic-bezier(.22,.68,0,1.05), opacity .2s ease",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "linear-gradient(135deg,var(--gold),#a07830)",
            color: "#1a0f00", fontFamily: "'Reem Kufi',sans-serif", fontSize: 12,
            fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
            padding: "12px 24px", boxShadow: "0 8px 24px rgba(201,168,76,.4)",
            clipPath: "polygon(6% 0,100% 0,94% 100%,0 100%)",
          }}>
            View Item <ChevronRight style={{ width: 13, height: 13 }} />
          </div>
        </div>
      </div>

      <div style={{ padding: "18px 18px 20px", display: "flex", flexDirection: "column", gap: 11, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <div style={{
            fontFamily: "'Cinzel',serif", fontSize: 14, fontWeight: 700,
            color: "var(--text)", lineHeight: 1.35,
          }}>{product.name}</div>
          <div style={{
            fontFamily: "'Cinzel Decorative',serif", fontSize: 14,
            color: "var(--goldL)", flexShrink: 0, letterSpacing: .5,
          }}>
            {price
              ? formatPrice(
                  displayPriceValue(price.value, product, firstPricedVariant),
                  price.currency
                )
              : ""}
          </div>
        </div>

        {product.description && (
          <ProductDescription
            html={product.description}
            compact
          />
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpenProduct(product);
          }}
          style={{
            marginTop: "auto", width: "100%", padding: "10px 0", borderRadius: 3, cursor: "pointer",
            border: "1px solid rgba(201,168,76,.4)", background: "rgba(201,168,76,.08)",
            color: "var(--goldL)", fontFamily: "'Reem Kufi',sans-serif",
            fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase",
          }}
        >
          Choose Options
        </button>
      </div>
    </div>
  );
}


// ─── Product detail modal ────────────────────────────────────────────────────
function ProductModal({
  product,
  open,
  onClose,
  onAddToCart,
  collectionId,
}) {
  const variants = product?.variants || [];

  const allColours = [];
  const seenColours = new Set();
  variants.forEach((v) => {
    const colour = v.attributes?.color;
    if (colour?.name && !seenColours.has(colour.name)) {
      seenColours.add(colour.name);
      allColours.push(colour);
    }
  });

  const hasColour = allColours.length > 0;
  const hasSize = variants.some((v) => v.attributes?.size?.name);

  const [colourName, setColourName] = useState(null);
  const [sizeName, setSizeName] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | loading | added | error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open || !product) return;
    setColourName(allColours[0]?.name || null);
    setSizeName(null);
    setImageIndex(0);
    setStatus("idle");
    setErrorMessage("");
  }, [open, product?.id]);
  
  useEffect(() => {
    if (!open) return;
    setSizeName(null);
    setImageIndex(0);
    setStatus("idle");
    setErrorMessage("");
  }, [colourName, open]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !product) return null;

  const colourVariants = variants.filter(
    (v) => !hasColour || v.attributes?.color?.name === colourName
  );

  const sizeOptions = [];
  const seenSizes = new Set();
  colourVariants.forEach((v) => {
    const size = v.attributes?.size;
    if (size?.name && !seenSizes.has(size.name)) {
      seenSizes.add(size.name);
      sizeOptions.push(size);
    }
  });

  let selectedVariant = null;
  if ((!hasColour || colourName) && (!hasSize || sizeName)) {
    selectedVariant = variants.find((v) =>
      (!hasColour || v.attributes?.color?.name === colourName) &&
      (!hasSize || v.attributes?.size?.name === sizeName)
    ) || null;
  }

  // Products without explicit size/colour options can resolve immediately.
  if (!hasColour && !hasSize && variants.length === 1) {
    selectedVariant = variants[0];
  }

  const groupedOrBundle =
    isGroupedOrBundleProduct(product, selectedVariant) ||
    String(product?.name || "").toLowerCase().includes("set");

  const outOfStock =
    !groupedOrBundle &&
    selectedVariant?.stock?.type === "LIMITED" &&
    Number(selectedVariant?.stock?.inStock ?? 0) <= 0;

  // Build the gallery from the selected colour immediately, even before a
  // size has been chosen. Once a size is selected, that exact variant's
  // images remain first in the gallery.
  const colourImages = colourVariants.flatMap((v) => v?.images || []);
  const rawImages = [
    ...(selectedVariant?.images || []),
    ...colourImages,
    ...(product.images || []),
  ];

  const images = [];
  const seenImageUrls = new Set();
  rawImages.forEach((img) => {
    const url = img?.url || img?.transformedUrl;
    if (url && !seenImageUrls.has(url)) {
      seenImageUrls.add(url);
      images.push({ ...img, displayUrl: url });
    }
  });

  const currentImage = images[imageIndex]?.displayUrl || null;
  const displayedPrice =
    selectedVariant?.unitPrice ||
    colourVariants.find((v) => v?.unitPrice)?.unitPrice ||
    variants.find((v) => v?.unitPrice)?.unitPrice;

  async function handleAdd() {
    if (hasColour && !colourName) {
      setErrorMessage("Please choose a colour.");
      return;
    }
    if (hasSize && !sizeName) {
      setErrorMessage("Please choose a size.");
      return;
    }
    if (!selectedVariant) {
      setErrorMessage("That option combination is unavailable.");
      return;
    }
    if (outOfStock) {
      setErrorMessage("That option is sold out.");
      return;
    }

    setStatus("loading");
    setErrorMessage("");
    try {
      await onAddToCart(selectedVariant.id);
      setStatus("added");
      setTimeout(() => {
        onClose();
        setStatus("idle");
      }, 700);
    } catch (err) {
      console.error(err);
      const message = String(err?.message || "");
      if (message.includes("CART_OFFER_NOT_AVAILABLE") || message.includes("currently unavailable")) {
        setErrorMessage("This colour/size combination is currently unavailable. Please choose another option.");
      } else {
        setErrorMessage(message || "Couldn't add this item. Please try again.");
      }
      setStatus("error");
    }
  }

  return (
    <div
      className="store-product-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed", inset: 0, zIndex: 120,
        background: "rgba(0,0,0,.78)", backdropFilter: "blur(7px)",
        display: "grid", placeItems: "center", padding: "24px 16px",
        overflowY: "auto",
      }}
    >
      <section
        className="store-product-modal"
        role="dialog"
        aria-modal="true"
        aria-label={product.name}
        style={{
          width: "min(1080px, 100%)", maxHeight: "min(820px, calc(100vh - 48px))",
          overflowY: "auto", position: "relative",
          background: "linear-gradient(160deg,var(--bg2) 0%,var(--bg) 68%,#090611 100%)",
          border: "1px solid var(--borderB)", borderRadius: 5,
          boxShadow: "0 35px 100px rgba(0,0,0,.72)",
        }}
      >
        <button
          onClick={onClose}
          className="store-cart-icon-btn store-product-modal-close"
          aria-label="Close product"
          style={{
            position: "absolute", top: 16, right: 16, zIndex: 10,
            width: 38, height: 38, borderRadius: "50%", cursor: "pointer",
            border: "1px solid var(--border)", background: "rgba(8,6,14,.78)",
            color: "var(--text2)", display: "grid", placeItems: "center",
          }}
        >
          <X style={{ width: 17, height: 17 }} />
        </button>

        <div className="store-product-grid" style={{
          display: "grid", gridTemplateColumns: "minmax(0,1.08fr) minmax(330px,.92fr)",
          minHeight: 600,
        }}>
          {/* Gallery */}
          <div className="store-product-gallery" style={{
            minHeight: 600, padding: "28px", borderRight: "1px solid var(--border)",
            background: "radial-gradient(ellipse at 50% 20%,rgba(123,94,167,.13),transparent 58%)",
          }}>
            <div className="store-product-image-main" style={{
              height: "min(530px, 58vh)", minHeight: 360,
              background: "linear-gradient(145deg,#141018,#0d0912)",
              border: "1px solid var(--border)", borderRadius: 4,
              overflow: "hidden", position: "relative", display: "grid", placeItems: "center",
            }}>
              {currentImage ? (
                <img
                  src={currentImage}
                  alt={`${product.name} ${imageIndex + 1}`}
                  style={{
                    width: "auto",
                    height: "auto",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    objectPosition: "center",
                    display: "block",
                    margin: "auto",
                  }}
                />
              ) : (
                <ShoppingBag style={{ width: 42, height: 42, color: "var(--muted)" }} />
              )}

              {images.length > 1 && (
                <>
                  <button
                    aria-label="Previous image"
                    onClick={() => setImageIndex((i) => (i - 1 + images.length) % images.length)}
                    style={{
                      position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                      width: 38, height: 38, borderRadius: "50%", border: "1px solid var(--border)",
                      background: "rgba(8,6,14,.72)", color: "var(--text)", cursor: "pointer",
                      display: "grid", placeItems: "center",
                    }}
                  >
                    <ChevronLeft style={{ width: 17, height: 17 }} />
                  </button>
                  <button
                    aria-label="Next image"
                    onClick={() => setImageIndex((i) => (i + 1) % images.length)}
                    style={{
                      position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                      width: 38, height: 38, borderRadius: "50%", border: "1px solid var(--border)",
                      background: "rgba(8,6,14,.72)", color: "var(--text)", cursor: "pointer",
                      display: "grid", placeItems: "center",
                    }}
                  >
                    <ChevronRight style={{ width: 17, height: 17 }} />
                  </button>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div style={{
                display: "flex", gap: 9, marginTop: 12, overflowX: "auto", paddingBottom: 3,
              }}>
                {images.map((img, i) => (
                  <button
                    key={`${img.displayUrl}-${i}`}
                    className="store-product-thumb"
                    onClick={() => setImageIndex(i)}
                    aria-label={`View image ${i + 1}`}
                    style={{
                      flex: "0 0 66px", width: 66, height: 72, padding: 0, cursor: "pointer",
                      borderRadius: 3, overflow: "hidden",
                      border: i === imageIndex ? "1px solid var(--gold)" : "1px solid var(--border)",
                      background: "var(--card)", opacity: i === imageIndex ? 1 : .68,
                    }}
                  >
                    <img src={img.displayUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", background: "#0d0912" }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product info/options */}
          <div className="store-product-info" style={{
            padding: "52px 38px 36px", display: "flex", flexDirection: "column",
            position: "relative",
          }}>
            <div style={{
              fontFamily: "'Reem Kufi',sans-serif", fontSize: 9, letterSpacing: 4,
              textTransform: "uppercase", color: "var(--gold)", marginBottom: 12,
            }}>
              Qadr Collection
            </div>

            <h2 style={{
              fontFamily: "'Cinzel Decorative',serif",
              fontSize: "clamp(25px,3vw,38px)", lineHeight: 1.15,
              margin: "0 0 13px", color: "var(--text)",
            }}>
              {product.name}
            </h2>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 14,
                flexWrap: "wrap",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontFamily: "'Cinzel Decorative',serif",
                  fontSize: 20,
                  color: "var(--goldL)",
                }}
              >
                {displayedPrice
                  ? formatPrice(
                      displayPriceValue(
                        displayedPrice.value,
                        product,
                        selectedVariant || colourVariants.find((v) => v?.unitPrice)
                      ),
                      displayedPrice.currency
                    )
                  : ""}
              </div>

              {collectionId && product?.id && (
                <ShareButton
                  compact
                  url={buildProductUrl(collectionId, product.id)}
                  title={product.name}
                  text={`Check out ${product.name} from The Ones Above.`}
                />
              )}
            </div>

            {product.description && (
              <ProductDescription html={product.description} />
            )}

            {hasColour && (
              <div style={{ marginBottom: 25 }}>
                <div style={{
                  fontFamily: "'Reem Kufi',sans-serif", fontSize: 10,
                  letterSpacing: 2.2, textTransform: "uppercase",
                  color: "var(--text2)", marginBottom: 11,
                }}>
                  Colour
                  {colourName && <span style={{ color: "var(--goldL)", letterSpacing: 0, textTransform: "none" }}> · {colourName}</span>}
                </div>

                <div style={{ display: "flex", gap: 11, flexWrap: "wrap" }}>
                  {allColours.map((colour) => {
                    const active = colour.name === colourName;
                    return (
                      <button
                        key={colour.name}
                        onClick={() => setColourName(colour.name)}
                        title={colour.name}
                        aria-label={`Colour ${colour.name}`}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 8,
                          padding: "8px 11px", borderRadius: 3, cursor: "pointer",
                          border: active ? "1px solid var(--gold)" : "1px solid var(--border)",
                          background: active ? "rgba(201,168,76,.09)" : "rgba(255,255,255,.015)",
                          color: active ? "var(--goldL)" : "var(--text2)",
                          fontFamily: "'Reem Kufi',sans-serif", fontSize: 10,
                        }}
                      >
                        <span style={{
                          width: 15, height: 15, borderRadius: "50%",
                          background: colour.swatch || "#888",
                          border: "1px solid rgba(255,255,255,.2)",
                          boxShadow: "0 1px 4px rgba(0,0,0,.5)",
                        }} />
                        {colour.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {hasSize && (
              <div style={{ marginBottom: 26 }}>
                <div style={{
                  fontFamily: "'Reem Kufi',sans-serif", fontSize: 10,
                  letterSpacing: 2.2, textTransform: "uppercase",
                  color: "var(--text2)", marginBottom: 11,
                }}>
                  Size
                  {sizeName && <span style={{ color: "var(--goldL)", letterSpacing: 0, textTransform: "none" }}> · {sizeName}</span>}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {sizeOptions.map((size) => {
                    const active = size.name === sizeName;
                    const matching = colourVariants.find((v) => v.attributes?.size?.name === size.name);
                    const soldOut =
                      matching?.stock?.type === "LIMITED" &&
                      Number(matching?.stock?.inStock ?? 0) <= 0;

                    return (
                      <button
                        key={size.name}
                        className="store-product-choice"
                        onClick={() => !soldOut && setSizeName(size.name)}
                        disabled={soldOut}
                        style={{
                          minWidth: 48, padding: "9px 13px", borderRadius: 3,
                          cursor: soldOut ? "not-allowed" : "pointer",
                          border: active ? "1px solid var(--gold)" : "1px solid var(--border)",
                          background: active ? "rgba(201,168,76,.1)" : "transparent",
                          color: soldOut ? "var(--muted)" : active ? "var(--goldL)" : "var(--text2)",
                          opacity: soldOut ? .45 : 1,
                          fontFamily: "'Reem Kufi',sans-serif", fontSize: 10, fontWeight: 700,
                        }}
                      >
                        {size.name}{soldOut ? " · Sold out" : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {errorMessage && (
              <div style={{
                marginBottom: 14, padding: "10px 12px",
                border: "1px solid rgba(210,90,90,.28)",
                background: "rgba(210,90,90,.07)",
                color: "#e0b3b3", borderRadius: 3,
                fontFamily: "'Reem Kufi',sans-serif", fontSize: 11, lineHeight: 1.5,
              }}>
                {errorMessage}
              </div>
            )}

            <div style={{ marginTop: "auto" }}>
              <button
                onClick={handleAdd}
                disabled={status === "loading" || outOfStock}
                style={{
                  width: "100%", padding: "15px 18px", borderRadius: 3,
                  border: "1px solid rgba(201,168,76,.6)",
                  background: outOfStock
                    ? "rgba(255,255,255,.03)"
                    : "linear-gradient(135deg,var(--gold),#987025)",
                  color: outOfStock ? "var(--muted)" : "#160d00",
                  cursor: status === "loading" || outOfStock ? "default" : "pointer",
                  fontFamily: "'Reem Kufi',sans-serif", fontSize: 11,
                  fontWeight: 800, letterSpacing: 2.1, textTransform: "uppercase",
                  boxShadow: outOfStock ? "none" : "0 12px 28px rgba(201,168,76,.18)",
                }}
              >
                {status === "loading"
                  ? "Adding…"
                  : status === "added"
                  ? "Added to Cart ✓"
                  : outOfStock
                  ? "Sold Out"
                  : hasSize && !sizeName
                  ? "Select a Size"
                  : "Add to Cart"}
              </button>

              <div style={{
                marginTop: 11, textAlign: "center",
                fontFamily: "'Reem Kufi',sans-serif", fontSize: 9,
                color: "var(--muted)", letterSpacing: .5,
              }}>
                Your exact colour and size are saved to the cart.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


// ─── Chapter card (collection tile) ────────────────────────────────────────
function ChapterCard({ col, count, onSelect }) {
  return (
    <div className="store-chapter" onClick={() => col.status === "live" && onSelect(col.id)} style={{
      background: col.bg, border: `1px solid ${col.borderColor}`, borderRadius: 4,
      padding: "38px 34px", position: "relative", overflow: "hidden",
      opacity: col.status === "soon" ? 0.62 : 1,
    }}>
      <span style={{ position: "absolute", top: 10, left: 10, zIndex: 2 }}><CornerMark rotate={-90} /></span>
      <span style={{ position: "absolute", bottom: 10, right: 10, zIndex: 2 }}><CornerMark rotate={90} /></span>

      <div style={{ position: "absolute", top: "-30%", left: "50%", transform: "translateX(-50%)", width: "80%", height: "60%",
        background: `radial-gradient(ellipse, ${col.accentGlow} 0%, transparent 70%)`, pointerEvents: "none" }} />

      <div style={{ fontSize: 9, fontFamily: "'Reem Kufi',sans-serif", letterSpacing: 4, color: col.accentColor, textTransform: "uppercase", marginBottom: 10, opacity: 0.85 }}>
        {col.label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12 }}>
        <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: "clamp(26px,3.5vw,42px)", fontWeight: 900, color: "#fff", lineHeight: 1.05, textShadow: `0 0 40px ${col.accentGlow}` }}>
          {col.name}
        </div>
        <div style={{ fontFamily: "'Reem Kufi',sans-serif", fontSize: 18, color: col.accentColor, opacity: 0.75 }}>{col.arabic}</div>
      </div>
      <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.75, marginBottom: 20, maxWidth: 340 }}>{col.desc}</p>

      {col.charity && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(201,168,76,.1)",
          border: "1px solid rgba(201,168,76,.28)", borderRadius: 3, padding: "5px 14px", marginBottom: 20,
          fontSize: 10, fontFamily: "'Reem Kufi',sans-serif", letterSpacing: 1.2, color: "var(--gold)" }}>
          <Heart style={{ width: 11, height: 11, fill: "var(--gold)", color: "var(--gold)" }} />
          100% of profits to charity
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {col.status === "live" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "'Reem Kufi',sans-serif", fontSize: 11,
            fontWeight: 700, letterSpacing: 2, color: col.accentColor, textTransform: "uppercase" }}>
            View Collection{typeof count === "number" ? ` · ${count}` : ""}
            <ChevronRight className="store-chev" style={{ width: 14, height: 14, transition: "transform .22s ease" }} />
          </div>
        ) : (
          <div style={{ fontSize: 10, fontFamily: "'Reem Kufi',sans-serif", letterSpacing: 3, color: "var(--muted)", textTransform: "uppercase" }}>Coming Soon</div>
        )}
      </div>

      <div aria-hidden style={{ position: "absolute", bottom: -26, right: -14, fontFamily: "'Reem Kufi',sans-serif",
        fontSize: 100, color: "rgba(255,255,255,.028)", userSelect: "none", pointerEvents: "none", lineHeight: 1 }}>
        {col.arabic}
      </div>
    </div>
  );
}

// ─── Cart button (fixed, always visible) ───────────────────────────────────
function CartButton({ count, onClick }) {
  return (
    <button onClick={onClick} disabled={count === 0} className="store-cart-btn" style={{
      height: 42,
      display: "flex", alignItems: "center", gap: 8,
      background: "var(--card)", border: "1px solid var(--border)", borderRadius: 999,
      padding: "0 17px", cursor: count === 0 ? "default" : "pointer",
      opacity: count === 0 ? 0.65 : 1, transition: "border-color .2s ease",
      fontFamily: "'Reem Kufi',sans-serif", fontSize: 11, letterSpacing: 1.5,
      color: "var(--text)", textTransform: "uppercase",
      boxShadow: "0 10px 30px rgba(0,0,0,.35)",
      whiteSpace: "nowrap",
    }}>
      <ShoppingBag style={{ width: 15, height: 15 }} />
      Cart ({count})
    </button>
  );
}

// ─── Store account button ────────────────────────────────────────────────────
// Uses the same auth state/modal as the rest of the website. Logged-out users
// get a Login / Sign Up button; logged-in users get their account/profile.
function AccountButton({
  authUser,
  onLogin,
  onAccount,
  onLogout,
}) {
  const loggedIn = Boolean(authUser);
  const [menuOpen, setMenuOpen] = useState(false);
  const accountRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;

    function handlePointerDown(event) {
      if (
        accountRef.current &&
        !accountRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  const displayName =
    authUser?.username ||
    authUser?.displayName ||
    authUser?.name ||
    authUser?.email?.split("@")?.[0] ||
    "Account";

  if (!loggedIn) {
    return (
      <button
        onClick={() => onLogin?.()}
        className="store-account-btn store-login-btn"
        style={{
          height: 42,
          display: "flex", alignItems: "center", gap: 8,
          background: "var(--card)", border: "1px solid var(--border)", borderRadius: 999,
          padding: "0 16px", cursor: "pointer",
          fontFamily: "'Reem Kufi',sans-serif", fontSize: 11, letterSpacing: 1.2,
          color: "var(--text)", boxShadow: "0 10px 30px rgba(0,0,0,.35)",
          whiteSpace: "nowrap", transition: "border-color .2s ease",
        }}
        aria-label="Login or sign up"
      >
        <LogIn
          style={{
            width: 15,
            height: 15,
            color: "var(--goldL)",
            flexShrink: 0,
          }}
        />

        <span className="store-login-desktop">
          Login / Sign Up
        </span>

        <span className="store-login-mobile">
          Login
        </span>
      </button>
    );
  }

  return (
    <div
      ref={accountRef}
      className="store-account-wrap"
    >
      <button
        onClick={() => setMenuOpen((open) => !open)}
        className="store-account-btn"
        style={{
          height: 42,
          display: "flex", alignItems: "center", gap: 8,
          background: "var(--card)", border: "1px solid var(--border)", borderRadius: 999,
          padding: "0 16px", cursor: "pointer",
          fontFamily: "'Reem Kufi',sans-serif", fontSize: 11, letterSpacing: 1.2,
          color: "var(--text)", boxShadow: "0 10px 30px rgba(0,0,0,.35)",
          whiteSpace: "nowrap", transition: "border-color .2s ease",
        }}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label="Account menu"
      >
        <UserRound style={{ width: 15, height: 15, color: "var(--goldL)" }} />
        <span className="store-account-name">{displayName}</span>
        <ChevronDown
          className="store-account-chevron"
          style={{
            width: 12,
            height: 12,
            color: "var(--muted)",
            transform: menuOpen ? "rotate(180deg)" : "none",
            transition: "transform .16s ease",
          }}
        />
      </button>

      {menuOpen && (
        <div className="store-account-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="store-account-menu-btn"
            onClick={() => {
              setMenuOpen(false);
              onAccount?.();
            }}
          >
            <Settings style={{ width: 14, height: 14, color: "var(--goldL)" }} />
            Settings
          </button>

          <div
            style={{
              height: 1,
              background: "var(--border)",
              margin: "4px 5px",
            }}
          />

          <button
            type="button"
            role="menuitem"
            className="store-account-menu-btn logout"
            onClick={async () => {
              setMenuOpen(false);

              if (typeof onLogout !== "function") {
                console.error(
                  "[Store account] Logout callback is not connected. Pass onLogout from App.jsx."
                );
                return;
              }

              await onLogout();
            }}
          >
            <LogOut style={{ width: 14, height: 14 }} />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Custom cart drawer ──────────────────────────────────────────────────────
function CartDrawer({
  open,
  cart,
  loading,
  updatingVariantId,
  error,
  currency,
  onClose,
  onChangeQuantity,
  onRemove,
  onCheckout,
}) {
  if (!open) return null;

  const items = cart?.items || [];
  const subtotal = items.reduce((total, item) => {
    const unit = Number(item.variant?.unitPrice?.value || 0);
    return total + unit * (item.quantity || 0);
  }, 0);
  const resolvedCurrency =
    items[0]?.variant?.unitPrice?.currency || currency || "GBP";

  return (
    <div
      className="store-cart-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(0,0,0,.68)", backdropFilter: "blur(5px)",
        display: "flex", justifyContent: "flex-end",
      }}
    >
      <aside
        className="store-cart-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
        style={{
          width: "min(440px, 100vw)", height: "100%",
          background: "linear-gradient(180deg,var(--bg2) 0%,var(--bg) 100%)",
          borderLeft: "1px solid var(--borderB)",
          boxShadow: "-24px 0 70px rgba(0,0,0,.55)",
          display: "flex", flexDirection: "column", position: "relative",
        }}
      >
        <div aria-hidden style={{
          position: "absolute", inset: 0, pointerEvents: "none", opacity: .32,
          background: "radial-gradient(ellipse 80% 35% at 50% 0%,rgba(123,94,167,.22),transparent 70%)",
        }} />

        <header style={{
          position: "relative", zIndex: 1, padding: "24px 24px 20px",
          borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
        }}>
          <div>
            <div style={{
              fontFamily: "'Reem Kufi',sans-serif", fontSize: 9,
              letterSpacing: 3.5, color: "var(--gold)", textTransform: "uppercase", marginBottom: 5,
            }}>Your order</div>
            <div style={{
              fontFamily: "'Cinzel Decorative',serif", fontSize: 22,
              fontWeight: 900, color: "var(--text)",
            }}>Cart</div>
          </div>
          <button
            onClick={onClose}
            className="store-cart-icon-btn"
            aria-label="Close cart"
            style={{
              width: 38, height: 38, borderRadius: "50%", cursor: "pointer",
              border: "1px solid var(--border)", background: "rgba(255,255,255,.025)",
              color: "var(--text2)", display: "grid", placeItems: "center", transition: ".2s",
            }}
          >
            <X style={{ width: 17, height: 17 }} />
          </button>
        </header>

        <div style={{
          position: "relative", zIndex: 1, flex: 1, overflowY: "auto", padding: 24,
        }}>
          {loading && (
            <div style={{
              minHeight: 220, display: "grid", placeItems: "center",
              color: "var(--muted)", fontFamily: "'Reem Kufi',sans-serif",
              fontSize: 10, letterSpacing: 2.5, textTransform: "uppercase",
            }}>
              <div style={{ textAlign: "center" }}>
                <ShoppingBag className="store-spinner" style={{ width: 22, height: 22, marginBottom: 12 }} />
                <div>Loading cart…</div>
              </div>
            </div>
          )}

          {!loading && error && (
            <div style={{
              padding: "18px 16px", border: "1px solid rgba(200,90,90,.3)",
              background: "rgba(200,90,90,.07)", color: "var(--text2)",
              fontFamily: "'Reem Kufi',sans-serif", fontSize: 12, lineHeight: 1.65,
            }}>{error}</div>
          )}

          {!loading && !error && items.length === 0 && (
            <div style={{
              minHeight: 260, display: "grid", placeItems: "center", textAlign: "center",
            }}>
              <div>
                <ShoppingBag style={{ width: 30, height: 30, color: "var(--muted)", marginBottom: 14 }} />
                <div style={{
                  fontFamily: "'Cinzel',serif", fontSize: 16, color: "var(--text)", marginBottom: 8,
                }}>Your cart is empty</div>
                <div style={{
                  fontFamily: "'Reem Kufi',sans-serif", fontSize: 11, color: "var(--muted)",
                }}>Add something from the collection to get started.</div>
              </div>
            </div>
          )}

          {!loading && !error && items.map((item) => {
            const variant = item.variant || {};
            const product = variant.product || {};
            const image = variant.images?.[0]?.url || variant.images?.[0]?.transformedUrl;
            const colour = variant.attributes?.color?.name;
            const size = variant.attributes?.size?.name;
            const busy = updatingVariantId === variant.id;
            const linePrice = Number(variant.unitPrice?.value || 0) * (item.quantity || 0);
            const lineCurrency = variant.unitPrice?.currency || resolvedCurrency;

            return (
              <div key={variant.id} style={{
                display: "grid", gridTemplateColumns: "88px 1fr", gap: 15,
                padding: "0 0 22px", marginBottom: 22, borderBottom: "1px solid var(--border)",
                opacity: busy ? .62 : 1, transition: "opacity .18s ease",
              }}>
                <div style={{
                  width: 88, height: 105, background: "var(--card)",
                  border: "1px solid var(--border)", borderRadius: 3, overflow: "hidden",
                }}>
                  {image ? (
                    <img src={image} alt={product.name || variant.name || "Cart item"} style={{
                      width: "100%", height: "100%", objectFit: "cover", display: "block",
                    }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
                      <ShoppingBag style={{ width: 20, height: 20, color: "var(--muted)" }} />
                    </div>
                  )}
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={{
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
                  }}>
                    <div style={{
                      fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: 13,
                      color: "var(--text)", lineHeight: 1.4,
                    }}>{product.name || variant.name || "Product"}</div>
                    <button
                      onClick={() => onRemove(variant.id)}
                      disabled={busy}
                      className="store-cart-icon-btn"
                      aria-label={`Remove ${product.name || "item"}`}
                      style={{
                        flexShrink: 0, width: 28, height: 28, borderRadius: "50%", cursor: busy ? "default" : "pointer",
                        border: "1px solid var(--border)", background: "transparent", color: "var(--muted)",
                        display: "grid", placeItems: "center", transition: ".2s",
                      }}
                    >
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </button>
                  </div>

                  {(colour || size) && (
                    <div style={{
                      marginTop: 5, fontFamily: "'Reem Kufi',sans-serif", fontSize: 10,
                      color: "var(--muted)", letterSpacing: .4,
                    }}>
                      {[colour, size].filter(Boolean).join(" · ")}
                    </div>
                  )}

                  <div style={{
                    marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                  }}>
                    <div style={{
                      display: "inline-flex", alignItems: "center", border: "1px solid var(--border)",
                      borderRadius: 999, overflow: "hidden", background: "rgba(255,255,255,.02)",
                    }}>
                      <button
                        onClick={() => onChangeQuantity(variant.id, item.quantity - 1)}
                        disabled={busy}
                        aria-label="Decrease quantity"
                        style={{
                          width: 32, height: 30, border: "none", background: "transparent",
                          color: "var(--text2)", cursor: busy ? "default" : "pointer", display: "grid", placeItems: "center",
                        }}
                      ><Minus style={{ width: 12, height: 12 }} /></button>
                      <span style={{
                        minWidth: 26, textAlign: "center", fontFamily: "'Reem Kufi',sans-serif",
                        fontSize: 11, color: "var(--text)",
                      }}>{item.quantity}</span>
                      <button
                        onClick={() => onChangeQuantity(variant.id, item.quantity + 1)}
                        disabled={busy}
                        aria-label="Increase quantity"
                        style={{
                          width: 32, height: 30, border: "none", background: "transparent",
                          color: "var(--text2)", cursor: busy ? "default" : "pointer", display: "grid", placeItems: "center",
                        }}
                      ><Plus style={{ width: 12, height: 12 }} /></button>
                    </div>

                    <div style={{
                      fontFamily: "'Cinzel Decorative',serif", fontSize: 13, color: "var(--goldL)",
                    }}>{formatPrice(linePrice, lineCurrency)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!loading && !error && items.length > 0 && (
          <footer style={{
            position: "relative", zIndex: 1, borderTop: "1px solid var(--border)",
            padding: "20px 24px 24px", background: "rgba(8,6,14,.72)", backdropFilter: "blur(10px)",
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 7,
            }}>
              <span style={{
                fontFamily: "'Reem Kufi',sans-serif", fontSize: 11, letterSpacing: 1.8,
                textTransform: "uppercase", color: "var(--text2)",
              }}>Subtotal</span>
              <span style={{
                fontFamily: "'Cinzel Decorative',serif", fontSize: 18, color: "var(--text)",
              }}>{formatPrice(subtotal, resolvedCurrency)}</span>
            </div>
            <div style={{
              fontFamily: "'Reem Kufi',sans-serif", fontSize: 10, color: "var(--muted)",
              marginBottom: 17, lineHeight: 1.5,
            }}>Shipping and taxes are calculated securely at checkout.</div>
            <button
              onClick={onCheckout}
              className="store-cart-checkout"
              style={{
                width: "100%", padding: "14px 18px", border: "1px solid rgba(201,168,76,.58)",
                background: "linear-gradient(135deg,var(--gold),#9b7228)", color: "#160d00",
                cursor: "pointer", fontFamily: "'Reem Kufi',sans-serif", fontSize: 11,
                fontWeight: 800, letterSpacing: 2.2, textTransform: "uppercase",
                boxShadow: "0 10px 28px rgba(201,168,76,.18)", transition: ".2s ease",
              }}
            >Proceed to Checkout</button>
          </footer>
        )}
      </aside>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function ClothingStorePage({
  onGoHome,
  authUser,
  accessToken,
  onOpenAuth,
  onOpenSettings,
  onLogout,
}) {
  const isStoreDomain = window.location.hostname === "store.theonesabove.com";

  function handleGoHome() {
    if (isStoreDomain) {
      window.location.href = "https://theonesabove.com";
      return;
    }

    if (typeof onGoHome === "function") {
      onGoHome();
      return;
    }

    window.location.href = "/";
  }

  const [activeCollection, setActiveCollection] = useState(null);
  const [cat, setCat] = useState("all");
  const styleRef = useRef(false);

  // Live product data, fetched from Fourthwall per live collection.
  const [productsByCollection, setProductsByCollection] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Currency is inferred from the shopper's browser locale/region.
  // UK -> GBP, euro-area -> EUR, everyone else -> USD.
  const [storeCurrency] = useState(() => detectStoreCurrency());

  // Cart — persisted so it survives a refresh.
  const readLocal = (key, fallback) => {
    try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
  };
  const [cartId, setCartId] = useState(() => readLocal("fw_cart_id", null));
  const [cartCount, setCartCount] = useState(() => Number(readLocal("fw_cart_count", 0)));
  const [cartCurrency, setCartCurrency] = useState(() => storeCurrency);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartData, setCartData] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState(null);
  const [updatingVariantId, setUpdatingVariantId] = useState(null);

  // Product detail modal — customer chooses exact colour/size before adding.
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (styleRef.current) return;
    styleRef.current = true;
    const tag = document.createElement("style");
    tag.textContent = CSS;
    document.head.appendChild(tag);
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [activeCollection]);

  // Do not let an old USD value from earlier testing override the visitor's
  // detected regional currency. Existing carts are re-read in this currency.
  useEffect(() => {
    setCartCurrency(storeCurrency);
    try {
      localStorage.setItem("fw_cart_currency", storeCurrency);
    } catch {}
  }, [storeCurrency]);

  useEffect(() => {
    if (!cartOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => { if (e.key === "Escape") setCartOpen(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [cartOpen]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingProducts(true);
      setLoadError(null);
      try {
        const live = COLLECTIONS.filter((c) => c.status === "live" && c.fourthwallHandle);
        const entries = await Promise.all(
          live.map(async (c) => [
            c.id,
            await fourthwall.getCollectionProducts(c.fourthwallHandle, storeCurrency),
          ])
        );
        if (!cancelled) setProductsByCollection(Object.fromEntries(entries));
      } catch (err) {
        console.error(err);
        if (!cancelled) setLoadError(err.message || "Couldn't load products from Fourthwall.");
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [storeCurrency]);


  // Open shared collection/product links after Fourthwall products are loaded.
  useEffect(() => {
    if (loadingProducts) return;

    const params = new URLSearchParams(window.location.search);
    const collectionId = params.get("collection");
    const productId = params.get("product");

    if (!collectionId) return;

    const collection = COLLECTIONS.find(
      (item) => item.id === collectionId && item.status === "live"
    );

    if (!collection) return;

    setActiveCollection(collectionId);
    setCat("all");

    if (!productId) {
      setSelectedProduct(null);
      return;
    }

    const products = productsByCollection[collectionId] || [];
    const product = products.find(
      (item) => String(item.id) === String(productId)
    );

    if (product) {
      setSelectedProduct(product);
    }
  }, [loadingProducts, productsByCollection]);

  // Keep browser Back/Forward navigation in sync with the collection/modal.
  useEffect(() => {
    function syncFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const collectionId = params.get("collection");
      const productId = params.get("product");

      const collection = COLLECTIONS.find(
        (item) => item.id === collectionId && item.status === "live"
      );

      if (!collection) {
        setActiveCollection(null);
        setSelectedProduct(null);
        setCat("all");
        return;
      }

      setActiveCollection(collection.id);
      setCat("all");

      if (!productId) {
        setSelectedProduct(null);
        return;
      }

      const products = productsByCollection[collection.id] || [];
      const product = products.find(
        (item) => String(item.id) === String(productId)
      );

      setSelectedProduct(product || null);
    }

    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, [productsByCollection]);

  function persistCart(cart) {
    const count = fourthwall.cartItemCount(cart);
    const currency =
      cart.items?.[0]?.variant?.unitPrice?.currency ||
      storeCurrency;

    setCartId(cart.id);
    setCartCount(count);
    setCartCurrency(currency);
    setCartData(cart);
    try {
      localStorage.setItem("fw_cart_id", cart.id);
      localStorage.setItem("fw_cart_count", String(count));
      localStorage.setItem("fw_cart_currency", currency);
    } catch {}
    return cart;
  }

  function clearStoredCart() {
    setCartId(null);
    setCartCount(0);
    setCartData(null);
    try {
      localStorage.removeItem("fw_cart_id");
      localStorage.removeItem("fw_cart_count");
      localStorage.removeItem("fw_cart_currency");
    } catch {}
  }

  async function handleAddToCart(variantId) {
    let cart;

    if (cartId) {
      try {
        // Verify the persisted cart still exists.
        await fourthwall.getCart(cartId, storeCurrency);

        // IMPORTANT: use Fourthwall's dedicated /add operation for a new
        // product/variant so existing line items stay in the same cart.
        cart = await fourthwall.addCartItems(
          cartId,
          [{ variantId, quantity: 1 }],
          storeCurrency
        );
      } catch (err) {
        const message = String(err?.message || "");

        // An unavailable offer is a real product/variant error; do not hide it
        // by creating a new cart.
        if (
          message.includes("CART_OFFER_NOT_AVAILABLE") ||
          message.includes("currently unavailable")
        ) {
          throw err;
        }

        // Otherwise assume the persisted cart expired and create a fresh cart.
        cart = await fourthwall.createCart(
          [{ variantId, quantity: 1 }],
          storeCurrency
        );
      }
    } else {
      cart = await fourthwall.createCart(
        [{ variantId, quantity: 1 }],
        storeCurrency
      );
    }

    persistCart(cart);
    return cart;
  }

  async function handleOpenCart() {
    if (!cartId || cartCount === 0) return;
    setCartOpen(true);
    setCartError(null);

    // If we already have fresh cart data from adding/updating an item, render it
    // instantly. Otherwise retrieve the persisted cart from Fourthwall.
    if (cartData?.id === cartId) return;

    setCartLoading(true);
    try {
      const cart = await fourthwall.getCart(cartId, storeCurrency);
      persistCart(cart);
    } catch (err) {
      console.error(err);
      clearStoredCart();
      setCartError("This cart has expired. Add an item again to start a new cart.");
    } finally {
      setCartLoading(false);
    }
  }

  async function handleChangeCartQuantity(variantId, quantity) {
    if (!cartId || updatingVariantId) return;
    if (quantity <= 0) {
      await handleRemoveCartItem(variantId);
      return;
    }

    setUpdatingVariantId(variantId);
    setCartError(null);
    try {
      const cart = await fourthwall.setCartItemQuantity(
        cartId,
        variantId,
        quantity,
        storeCurrency
      );
      persistCart(cart);
    } catch (err) {
      console.error(err);
      setCartError(err.message || "Couldn't update that item. Please try again.");
    } finally {
      setUpdatingVariantId(null);
    }
  }

  async function handleRemoveCartItem(variantId) {
    if (!cartId || updatingVariantId) return;
    setUpdatingVariantId(variantId);
    setCartError(null);
    try {
      const cart = await fourthwall.removeCartItem(
        cartId,
        variantId,
        storeCurrency
      );
      persistCart(cart);
    } catch (err) {
      console.error(err);
      setCartError(err.message || "Couldn't remove that item. Please try again.");
    } finally {
      setUpdatingVariantId(null);
    }
  }

  async function handleCheckout() {
    if (!cartId || cartCount === 0) return;

    if (!authUser) {
      setCartOpen(false);

      if (typeof onOpenAuth === "function") {
        onOpenAuth("login");
      }

      return;
    }

    if (!accessToken) {
      setCartError(
        "Your login session could not be verified. Please log in again."
      );
      return;
    }

    try {
      setCartError(null);

      // Securely associate this Fourthwall cart with the authenticated
      // TOA account. The backend derives the user id from the verified JWT;
      // the browser never sends or chooses a user id.
      await fourthwall.claimCart(
        cartId,
        accessToken,
        storeCurrency
      );

      window.location.href =
        fourthwall.getCheckoutUrl(
          cartId,
          storeCurrency
        );
    } catch (err) {
      console.error(
        "[Merch checkout]",
        err
      );

      const message = String(err?.message || "");

      if (
        message.includes("401") ||
        message.toLowerCase().includes("access token")
      ) {
        setCartError(
          "Your login session has expired. Please log in again."
        );

        setCartOpen(false);

        if (typeof onOpenAuth === "function") {
          onOpenAuth("login");
        }

        return;
      }

      setCartError(
        "We couldn't prepare your cart for checkout. Please try again."
      );
    }
  }

  const currentCollection = COLLECTIONS.find((c) => c.id === activeCollection);
  const collectionProducts = productsByCollection[activeCollection] || [];
  const visible = collectionProducts
    .filter(
      (p) => cat === "all" || inferCategories(p.name).includes(cat)
    )
    .sort((a, b) => {
      const priorityDifference =
        getProductHighlightPriority(activeCollection, b) -
        getProductHighlightPriority(activeCollection, a);

      if (priorityDifference !== 0) {
        return priorityDifference;
      }

      return 0;
    });

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh", color: "var(--text)", position: "relative", overflowX: "hidden" }}>
      <GirihField />

      {/* Store account + cart controls */}
      <div
        className="store-top-actions"
        style={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 30,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <AccountButton
          authUser={authUser}
          onLogin={() => onOpenAuth?.("login")}
          onAccount={() => onOpenSettings?.()}
          onLogout={() => onLogout?.()}
        />
        <CartButton count={cartCount} onClick={handleOpenCart} />
      </div>
      <CartDrawer
        open={cartOpen}
        cart={cartData}
        loading={cartLoading}
        updatingVariantId={updatingVariantId}
        error={cartError}
        currency={cartCurrency}
        onClose={() => setCartOpen(false)}
        onChangeQuantity={handleChangeCartQuantity}
        onRemove={handleRemoveCartItem}
        onCheckout={handleCheckout}
      />
      <ProductModal
        product={selectedProduct}
        open={Boolean(selectedProduct)}
        onClose={() => {
          setSelectedProduct(null);

          if (activeCollection) {
            replaceStoreUrl({
              collectionId: activeCollection,
              productId: null,
            });
          } else {
            replaceStoreUrl({
              collectionId: null,
              productId: null,
            });
          }
        }}
        onAddToCart={handleAddToCart}
        collectionId={activeCollection}
      />

      {/* Ambient glows */}
      <div aria-hidden style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}>
        <div style={{ position: "absolute", top: "0%", left: "50%", transform: "translateX(-50%)", width: "100%", height: "55%",
          background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(123,94,167,.1) 0%, transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: 0, right: "-10%", width: "50%", height: "40%",
          background: "radial-gradient(ellipse, rgba(201,168,76,.05) 0%, transparent 65%)" }} />
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>

        {/* ══ VIEW A: LANDING ══ */}
        {!activeCollection && (
          <>
            <div
              className="store-hero-header"
              style={{
                padding: "0 40px",
                textAlign: "center",
                position: "relative",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1,
                background: "linear-gradient(90deg,transparent,var(--purple),var(--gold),transparent)" }} />

              <FadeIn
                delay={60}
                style={{
                  marginBottom: 0,
                }}
              >
                <div className="store-logo-wrap">
                <img
                  src={LOGO_SRC}
                  alt="Store logo"
                  style={{
                    width: "clamp(300px, 46vw, 620px)",
                    maxWidth: "92vw",
                    height: "auto",
                    margin: "0 auto",
                    display: "block",
                  }}
                />
                </div>
              </FadeIn>

              <FadeIn delay={120} style={{ marginTop: 0 }}>
                <div
                  style={{
                    fontFamily: "'Cinzel',serif",
                    fontSize: "clamp(14px, 1.8vw, 19px)",
                    color: "var(--text2)",
                    fontStyle: "italic",
                    letterSpacing: 0.4,
                    marginBottom: 3,
                  }}
                >
                  Official apparel &amp; accessories.
                </div>
              </FadeIn>

              <FadeIn delay={180}>
                <div className="store-manifesto" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
                  {["Premium Materials", "Limited Drops", "Profits Support Charity"].map((line, i, arr) => (
                    <span key={line} style={{ display: "flex", alignItems: "center", gap: 20 }}>
                      <span style={{ fontFamily: "'Reem Kufi',sans-serif", fontSize: 10, letterSpacing: 1.5, color: "var(--muted)", textTransform: "uppercase" }}>{line}</span>
                      {i < arr.length - 1 && <span className="store-mdiv" style={{ color: "var(--borderB)", fontSize: 10 }}>◆</span>}
                    </span>
                  ))}
                </div>
              </FadeIn>
            </div>

            <div style={{ padding: "34px 0" }}><Frieze /></div>

            <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 36px 100px" }}>
              <FadeIn delay={0}>
                <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: "clamp(26px,3.4vw,38px)", fontWeight: 900, color: "var(--text)", marginBottom: 30 }}>Collections</div>
              </FadeIn>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20 }}>
                {COLLECTIONS.map((col, i) => (
                  <FadeIn key={col.id} delay={i * 90}>
                    <ChapterCard
                      col={col}
                      count={col.status === "live" ? productsByCollection[col.id]?.length : undefined}
                      onSelect={(collectionId) => {
                        setActiveCollection(collectionId);
                        setSelectedProduct(null);
                        setCat("all");

                        replaceStoreUrl({
                          collectionId,
                          productId: null,
                        });
                      }}
                    />
                  </FadeIn>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ══ VIEW B: SINGLE COLLECTION ══ */}
{activeCollection && currentCollection && (
  <>
    <div
      style={{
        position: "relative",
        padding: "70px 40px 56px",
        textAlign: "center",
        borderBottom: "1px solid var(--border)",
        overflow: "hidden",
      }}
    >
      {/* Artistic wash instead of a flat colour block: layered, soft-edged
          glows in the collection's accent colour, plus a fade to the page's
          own background at the edges so it blends with the girih texture
          and ambient glows already sitting behind the whole page. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(
              ellipse 65% 75% at 22% -15%,
              ${currentCollection.accentGlow} 0%,
              transparent 60%
            ),
            radial-gradient(
              ellipse 55% 65% at 82% 115%,
              ${currentCollection.accentGlow} 0%,
              transparent 60%
            ),
            radial-gradient(
              ellipse 90% 90% at 50% 50%,
              transparent 40%,
              var(--bg) 100%
            )
          `,
        }}
      />

      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.5,
          background:
            "linear-gradient(180deg, var(--bg2) 0%, transparent 35%, transparent 65%, var(--bg2) 100%)",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `linear-gradient(
            90deg,
            transparent,
            ${currentCollection.accentColor},
            transparent
          )`,
        }}
      />

      {/* Back to all collections */}
      <FadeIn delay={0}>
        <button
          className="store-back"
          onClick={() => {
            setActiveCollection(null);
            setSelectedProduct(null);
            setCat("all");

            replaceStoreUrl({
              collectionId: null,
              productId: null,
            });
          }}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: "'Reem Kufi',sans-serif",
            fontSize: 10,
            letterSpacing: 2,
            color: "var(--text2)",
            textTransform: "uppercase",
            marginBottom: 26,
            opacity: 0.85,
            position: "relative",
            zIndex: 1,
          }}
        >
          <ArrowLeft
            style={{
              width: 12,
              height: 12,
            }}
          />
          All Collections
        </button>
      </FadeIn>

      {/* Collection label */}
      <FadeIn delay={40}>
        <div
          style={{
            fontSize: 10,
            fontFamily: "'Reem Kufi',sans-serif",
            letterSpacing: 6,
            color: currentCollection.accentColor,
            textTransform: "uppercase",
            marginBottom: 12,
            opacity: 0.85,
            position: "relative",
            zIndex: 1,
          }}
        >
          {currentCollection.label}
        </div>
      </FadeIn>

      {/* Collection title + Arabic */}
      <FadeIn delay={90}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "center",
            gap: 16,
            flexWrap: "wrap",
            position: "relative",
            zIndex: 1,
          }}
        >
          <h1
            style={{
              fontFamily: "'Cinzel Decorative',serif",
              fontSize: "clamp(50px,8.5vw,110px)",
              fontWeight: 900,
              lineHeight: 1,
              margin: 0,
              background:
                "linear-gradient(140deg,#fff 0%,var(--goldL) 35%,var(--purpleB) 70%,#d0c0ff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: `drop-shadow(
                0 0 60px
                ${currentCollection.accentGlow}
              )`,
            }}
          >
            {currentCollection.name}
          </h1>

          <span
            style={{
              fontFamily: "'Reem Kufi',sans-serif",
              fontSize: "clamp(22px,3.5vw,34px)",
              color: currentCollection.accentColor,
              opacity: 0.75,
            }}
          >
            {currentCollection.arabic}
          </span>
        </div>
      </FadeIn>

      {/* Tagline */}
      <FadeIn delay={140}>
        <p
          style={{
            fontFamily: "'Cinzel',serif",
            fontSize: "clamp(12px,1.5vw,14px)",
            color: "var(--text2)",
            letterSpacing: 3,
            fontStyle: "italic",
            marginTop: 16,
            marginBottom: currentCollection.charity ? 26 : 0,
            position: "relative",
            zIndex: 1,
          }}
        >
          {currentCollection.tagline}
        </p>
      </FadeIn>

      {/* Charity notice */}
      {currentCollection.charity && (
        <FadeIn delay={190}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              background: "rgba(201,168,76,.08)",
              border: "1px solid rgba(201,168,76,.3)",
              borderRadius: 3,
              padding: "10px 22px",
              fontFamily: "'Reem Kufi',sans-serif",
              fontSize: 11,
              letterSpacing: 1.5,
              color: "var(--gold)",
              textTransform: "uppercase",
              animation:
                "store-pulse 3s ease-in-out infinite",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Heart
              style={{
                width: 13,
                height: 13,
                fill: "var(--gold)",
                color: "var(--gold)",
              }}
            />

            100% of profits from this collection go to charity
          </div>
        </FadeIn>
      )}

      <FadeIn delay={230}>
        <div
          style={{
            marginTop: 18,
            position: "relative",
            zIndex: 2,
          }}
        >
          <ShareButton
            copyOnly
            url={buildCollectionUrl(currentCollection.id)}
            title={`${currentCollection.name} Collection`}
            text={`View the ${currentCollection.name} Collection from The Ones Above.`}
          />
        </div>
      </FadeIn>
    </div>

    {/* Decorative divider */}
    <div
      style={{
        padding: "26px 0 6px",
      }}
    >
      <Frieze />
    </div>

    {/* Products section */}
    <div
      style={{
        maxWidth: 1260,
        margin: "0 auto",
        padding: "44px 36px 90px",
      }}
    >
      {/* Product count + category filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 40,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "'Reem Kufi',sans-serif",
              fontSize: 10,
              letterSpacing: 3,
              color: "var(--gold)",
              textTransform: "uppercase",
              opacity: 0.8,
              marginBottom: 4,
            }}
          >
            The Drop
          </div>

          <div
            style={{
              fontFamily: "'Cinzel',serif",
              fontSize: 18,
              fontWeight: 700,
              color: "var(--text)",
            }}
          >
            {loadingProducts
              ? "Loading…"
              : `${visible.length} piece${
                  visible.length !== 1 ? "s" : ""
                }`}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          {CATEGORIES.map((id, i) => {
            const active = cat === id;

            return (
              <button
                key={id}
                onClick={() => setCat(id)}
                className="store-tab"
                style={{
                  position: "relative",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "6px 2px",
                  fontFamily: "'Reem Kufi',sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: 1.8,
                  textTransform: "uppercase",
                  color: active
                    ? "var(--text)"
                    : "var(--text2)",
                }}
              >
                {id}

                <span
                  className="store-tab-line"
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 2,
                    borderRadius: 2,
                    background: active
                      ? "linear-gradient(90deg,var(--gold),var(--purpleB))"
                      : TILE_COLORS[
                          i % TILE_COLORS.length
                        ],
                    opacity: active ? 1 : 0,
                    transition: ".2s",
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading state */}
      {loadingProducts && (
        <div
          style={{
            textAlign: "center",
            padding: "80px 0",
            color: "var(--muted)",
            fontFamily: "'Reem Kufi',sans-serif",
            letterSpacing: 3,
            fontSize: 11,
            textTransform: "uppercase",
          }}
        >
          <ShoppingBag
            className="store-spinner"
            style={{
              width: 22,
              height: 22,
              marginBottom: 12,
              display: "inline-block",
            }}
          />

          <div>Loading products…</div>
        </div>
      )}

      {/* Error state */}
      {!loadingProducts && loadError && (
        <div
          style={{
            textAlign: "center",
            padding: "60px 0",
            color: "var(--text2)",
            fontFamily: "'Reem Kufi',sans-serif",
            fontSize: 12,
            maxWidth: 480,
            margin: "0 auto",
            lineHeight: 1.7,
          }}
        >
          Couldn't load this collection from Fourthwall —{" "}
          {loadError}. Double-check
          REACT_APP_FW_STOREFRONT_TOKEN and the collection's
          fourthwallHandle in ClothingStorePage.jsx.
        </div>
      )}

      {/* Product grid */}
      {!loadingProducts && !loadError && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fill, minmax(290px, 1fr))",
            gap: 22,
          }}
        >
          {visible.map((p, i) => (
            <FadeIn
              key={p.id}
              delay={i * 55}
            >
              <ProductCard
                product={p}
                watermark={
                  currentCollection.arabic
                }
                collectionId={currentCollection.id}
                onOpenProduct={(product) => {
                  setSelectedProduct(product);

                  replaceStoreUrl({
                    collectionId: currentCollection.id,
                    productId: product.id,
                  });
                }}
              />
            </FadeIn>
          ))}
        </div>
      )}

      {/* Empty category */}
      {!loadingProducts &&
        !loadError &&
        visible.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "80px 0",
              color: "var(--muted)",
              fontFamily: "'Reem Kufi',sans-serif",
              letterSpacing: 3,
              fontSize: 11,
              textTransform: "uppercase",
            }}
          >
            Nothing in this category yet.
          </div>
        )}
    </div>
  </>
)}

        <Footer />
      </div>
    </div>
  );
}